import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange, linkedSignal } from '@angular/core';
import { Router } from '@angular/router';
import { DocumentGenerationDialogComponent } from '../dialogs/document-generation-dialog/document-generation-dialog.component';
import { DocumentUploadDialogComponent } from '../dialogs/document-upload-dialog/document-upload-dialog.component';
import { Entity } from '../entity/entity';
import { DocumentService } from '../services/document-service';
import { EntityService } from '../services/entity-service';
import { OrderService } from '../services/order-service';
import { PersonLoginService } from '../services/person-login-service';
import { QuoteService } from '../services/quote-service';
import { StringUtils } from '../utils/string-utils';
import { CreditDocument, Document } from './document';
import { DocumentManagementTab } from './document-management-tab';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScheduleService } from '../services/schedule.service';
import { Observable } from 'rxjs';
import { FileUtils } from '../utils/file-utils';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-document-management',
  templateUrl: './document-management.component.html',
  styleUrls: ['./document-management.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinner, MatButton, MatIcon, MatTabGroup, MatTab, DatePipe],
})
export class DocumentManagementComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly documentService = inject(DocumentService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly quoteService = inject(QuoteService);
  private readonly orderService = inject(OrderService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly scheduleService = inject(ScheduleService);
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  readonly tab = input<string>(undefined!);
  readonly entityType = input.required<string>({ alias: 'entity-type' });
  readonly entity = signal<Entity>(undefined!);
  readonly documents = signal<Document[]>(undefined!);
  readonly recentDocuments = signal<Document[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly selectedTab = linkedSignal(() => this.getSelectedTabIndex());
  readonly creditDocuments = signal<CreditDocument[]>(undefined!);
  readonly isSchedule = signal<boolean>(undefined!);

  ngOnInit(): void {
    this.resetStatus();
    this.personLoginService.getCachedLogin();
    this.initialize();
  }

  loadSelectedTab(): void {
    this.router.navigate([], { queryParams: { tab: this.getTabName() } });
  }

  isInCreditGroup() {
    return this.personLoginService.getCachedLogin()!.groups.filter((group) => 'CREDIT' === group.name).length > 0;
  }

  public backToEntity(): void {
    if (this.isSchedule()) {
      this.router.navigate(['schedules', this.entity().id.entityId]);
    } else if (this.entity().id.entityName === 'QUOTE') {
      this.router.navigate(['quotes', this.entity().id.entityId, this.entity().id.entityRevision]);
    } else {
      this.router.navigate(['orders', this.entity().id.entityId, this.entity().id.entityRevision]);
    }
  }

  public download(target: Document) {
    this.resetStatus();
    this.documentService.downloadDocument(target).subscribe({
      next: (response) => {
        this.isBusy.set(false);
        FileUtils.downloadFile(response, target.fileName);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  showUploadDialog(): void {
    const uploadDialog = this.dialog.open(DocumentUploadDialogComponent, {
      width: '820px',
      height: '480px',
      data: { entityKey: this.entity().id, documentType: this.getTabName(), brandID: this.entity().brandID },
    });
    uploadDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.loadDocuments();
      }
    });
  }

  showGenerateDialog(): void {
    if (this.isSchedule()) {
      this.openGenerateDialog();
    } else if (this.entity().id.entityName === 'QUOTE') {
      this.runQuoteMultiplierAndGenerate();
    } else {
      this.runContractMultiplierAndGenerate();
    }
  }

  approveCreditDocument(document: CreditDocument) {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentService.approveCreditDocument(document).subscribe({
      next: () => {
        this.isBusy.set(false);
        document.status = 'Approved';
      },
      error: (error) => this.handleError(error),
    });
  }

  denyCreditDocument(document: CreditDocument) {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentService.denyCreditDocument(document).subscribe({
      next: () => {
        this.isBusy.set(false);
        document.status = 'Denied';
      },
      error: (error) => this.handleError(error),
    });
  }

  private getSelectedTabIndex(): number {
    const index = DocumentManagementTab[this.tab() as keyof typeof DocumentManagementTab];
    if (typeof index === 'number') {
      return index;
    }
    return DocumentManagementTab.all;
  }

  private initialize(): void {
    this.readPathParameters();
    if (!this.tab()) {
      this.router.navigate([], { queryParams: { tab: 'all' } });
    }
  }

  private readPathParameters(): void {
    this.initializeFromRouteParams();
  }

  private loadDocuments(): void {
    this.isBusy.set(true);
    switch (this.selectedTab()) {
      case DocumentManagementTab.all:
        this.documentService.getEntityDocuments(this.entity().id.entityId, this.entity().id.entityRevision).subscribe({
          next: (result) => {
            this.documents.set(result);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
        break;
      case DocumentManagementTab.credit:
        this.documentService.getEntityDocuments(this.entity().id.entityId, this.entity().id.entityRevision).subscribe({
          next: (allDocs) => {
            if (allDocs && allDocs.length > 0) {
              this.entityService.getCreditDocuments(this.entity().id).subscribe({
                next: (creditDocs) => {
                  if (creditDocs && creditDocs.length > 0) {
                    for (const creditDoc of creditDocs) {
                      const matchingDocs = allDocs.filter((doc) => creditDoc.fileName === doc.fileName);
                      if (matchingDocs && matchingDocs.length > 0) {
                        const matchingDoc = matchingDocs[0];
                        creditDoc.documentName = matchingDoc.documentName;
                        creditDoc.path = matchingDoc.path;
                        creditDoc.uploadSource = matchingDoc.uploadSource;
                        creditDoc.userName = creditDoc.updateUser;
                        creditDoc.lastUpdate = new Date(creditDoc.updateTimestamp);
                        creditDoc.documentType = matchingDoc.documentType;
                      }
                    }
                    this.creditDocuments.set(creditDocs);
                  }
                  this.isBusy.set(false);
                },
                error: (error) => this.handleError(error),
              });
            }
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
        break;
      case DocumentManagementTab.recent:
        this.documentService.getEntityDocuments(this.entity().id.entityId, this.entity().id.entityRevision).subscribe({
          next: (result) => {
            this.documents.set(result);
            this.documents().sort((a, b) => {
              if (a.documentType < b.documentType) return 1;
              if (a.documentType > b.documentType) return -1;
              if (a.lastUpdate < b.lastUpdate) return 1;
              if (a.lastUpdate > b.lastUpdate) return -1;
              return 0;
            });
            const recentDocumentMap = new Map();
            for (const doc of this.documents()) {
              if (!recentDocumentMap.has(doc.documentType)) {
                recentDocumentMap.set(doc.documentType, doc);
              }
            }
            this.recentDocuments.set(Array.from(recentDocumentMap.values()));
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
        break;
    }
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private backToMain(): void {
    this.router.navigate(['main']);
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
  }

  private getTabName(): string {
    return DocumentManagementTab[this.selectedTab()];
  }

  private runQuoteMultiplierAndGenerate(): void {
    this.isBusy.set(true);
    this.quoteService.runMultiplier(this.entity()).subscribe({
      next: () => {
        this.isBusy.set(false);
        this.openGenerateDialog();
      },
      error: () => {
        this.isBusy.set(false);
        this.openGenerateDialog();
      },
    });
  }

  private runContractMultiplierAndGenerate(): void {
    this.isBusy.set(true);
    this.orderService.runMultiplier(this.entity().id).subscribe({
      next: () => {
        this.isBusy.set(false);
        this.openGenerateDialog();
      },
      error: () => {
        this.isBusy.set(false);
        this.openGenerateDialog();
      },
    });
  }

  private openGenerateDialog(): void {
    const generateDialog = this.dialog.open(DocumentGenerationDialogComponent, {
      data: {
        entity: this.entity(),
        allLines: this.entity().lines.map((line) => line.lineId),
      },
    });
    generateDialog.afterClosed().subscribe((result: string) => {
      if (result && result.length > 0) {
        this.snackBar.open(result, 'Close', {
          panelClass: ['documentTimeoutSnackBarMessage'],
          duration: 15000,
        });
      } else {
        this.isBusy.set(true);
        this.loadDocuments();
      }
    });
  }

  showApprovalButtons(): boolean {
    const creditDocs = this.creditDocuments() ?? [];
    for (const doc of creditDocs) {
      if (this.showDocumentApprovalButtons(doc)) {
        return true;
      }
    }
    return false;
  }

  showDocumentApprovalButtons(document: CreditDocument): boolean {
    const documentStatus = document?.status ?? '';
    return this.isInCreditGroup() && 'Pending Approval' === documentStatus;
  }

  private initializeFromRouteParams(): void {
    const entityType = this.entityType();
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      this.isSchedule.set('SCHEDULE' === entityType);
      let entityObservable: Observable<Entity> = this.entityService.getEntity(entityType, idString, +revisionString);
      if (this.isSchedule()) {
        entityObservable = this.scheduleService.getSchedule(idString);
      }
      entityObservable.subscribe({
        next: (entity) => {
          this.entity.set(entity);
          this.loadDocuments();
        },
        error: (error) => this.handleError(error),
      });
    } else {
      this.backToMain();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initializeFromRouteParams();
    }
  }
}
